import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NavigationEnd, Router } from '@angular/router';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { DirtyFormRegistry } from './dirty-form-registry.service';

const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Keeps long-lived PWA sessions on the latest deployment without user intervention: polls the
 * server for a new build (on startup, every 15 minutes, and when the tab regains focus) and
 * reloads automatically once the new version is downloaded. The reload is deferred only while a
 * form holds unsaved edits, then retried on navigation, tab focus, or the next poll tick.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly router = inject(Router);
  private readonly dirtyForms = inject(DirtyFormRegistry);

  private reloadPending = false;

  init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.reloadWhenSafe());

    // A corrupted/partial cache cannot self-heal; a full reload re-fetches everything.
    this.swUpdate.unrecoverable.subscribe(() => document.location.reload());

    concat(
      this.appRef.isStable.pipe(first((stable) => stable)),
      interval(UPDATE_CHECK_INTERVAL_MS)
    ).subscribe(() => this.reloadPendingOrCheck());

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.reloadPendingOrCheck();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.reloadPending) {
        this.reloadWhenSafe();
      }
    });
  }

  private reloadPendingOrCheck(): void {
    if (this.reloadPending) {
      this.reloadWhenSafe();
    } else {
      void this.swUpdate.checkForUpdate().catch(() => undefined);
    }
  }

  private reloadWhenSafe(): void {
    if (this.dirtyForms.isAnyDirty()) {
      this.reloadPending = true;
      return;
    }
    document.location.reload();
  }
}
